const readBrandingValue = (value: string | undefined, fallback: string): string => {
  const normalizedValue = value?.trim()
  return normalizedValue && normalizedValue.length > 0 ? normalizedValue : fallback
}

export interface BrandingConfig {
  appName: string
  /** Slogan primário — usado em splash, login e materiais de marca. */
  slogan: string
  /** Slogan secundário — descrição técnica/pilares; usado em locais informativos. */
  sloganSecondary: string
  iconPath: string
  /** Traço do logo sem fundo — tema claro (sidebar, splash adaptativo). */
  iconTransparentPath: string
  /** Traço claro sem fundo — tema escuro e splash sobre gradiente. */
  iconTransparentDarkPath: string
  /** Variante light do ícone (fundo branco + traços teal) — para fundos claros (login, emails). */
  iconLightPath: string
  /** Squircle escuro + traço (favicon dark, sidebar tema escuro). */
  iconAppPath: string
  /** Squircle branco fixo + traço teal (sidebar/login em ambos os temas). */
  iconAppLightPath: string
  /** Ícone PNG para apple-touch-icon (iOS/PWA). */
  appleTouchIconPath: string
  description: string
  themeColor: string
  /** Cor do fundo do ícone dark (manifest theme/background). Usada em textos de brand para harmonizar. */
  iconBrandColor: string
  /** Fundo do splash web e nativo (Capacitor). */
  splashBackgroundColor: string
  /** Ícone squircle para push web/FCM (192px). */
  pushNotificationIconPath: string
  /** Badge squircle para push web/FCM (96px). */
  pushNotificationBadgePath: string
}

export const branding: BrandingConfig = {
  appName: readBrandingValue(import.meta.env.VITE_BRAND_NAME, 'Synap'),
  slogan: readBrandingValue(
    import.meta.env.VITE_BRAND_SLOGAN,
    'Memória que cuida. Vida que conecta.',
  ),
  sloganSecondary: readBrandingValue(
    import.meta.env.VITE_BRAND_SLOGAN_SECONDARY,
    'Anamnese • Inteligência • Exames • Qualidade de vida • Ciclo completo',
  ),
  iconPath: readBrandingValue(import.meta.env.VITE_BRAND_ICON_PATH, '/synap-icon.svg'),
  iconTransparentPath: readBrandingValue(
    import.meta.env.VITE_BRAND_ICON_TRANSPARENT_PATH,
    '/synap-icon-transparent.svg',
  ),
  iconTransparentDarkPath: readBrandingValue(
    import.meta.env.VITE_BRAND_ICON_TRANSPARENT_DARK_PATH,
    '/synap-icon-mono.svg',
  ),
  iconLightPath: readBrandingValue(import.meta.env.VITE_BRAND_ICON_LIGHT_PATH, '/synap-icon-light.svg'),
  /** Emails Auth (Supabase GoTrue): templates usam `{{ .SiteURL }}/synap-icon-light.svg` — gradiente BR-001 `#103B4A` → `#0fa38f`; regenerado por `generate-icons.mjs`. */
  iconAppPath: readBrandingValue(import.meta.env.VITE_BRAND_ICON_APP_PATH, '/synap-icon-app.svg'),
  iconAppLightPath: readBrandingValue(
    import.meta.env.VITE_BRAND_ICON_APP_LIGHT_PATH,
    '/synap-icon-app-light.svg',
  ),
  appleTouchIconPath: '/apple-touch-icon.png',
  description: readBrandingValue(
    import.meta.env.VITE_BRAND_DESCRIPTION,
    'Plataforma inteligente de saúde que conecta histórico clínico, anamnese, exames e qualidade de vida ao longo de todo o ciclo da vida.',
  ),
  /** Cor do tema OS/PWA (barra de status, splash). Preferir em chrome interativo e `--color-mint-strong` na UI clínica. */
  themeColor: readBrandingValue(import.meta.env.VITE_BRAND_THEME_COLOR, '#0FA38F'),
  /** Teal mais escuro — wordmark, mask-icon e superfícies de marca estática. Não usar em chips de status clínico. */
  iconBrandColor: readBrandingValue(import.meta.env.VITE_BRAND_ICON_BRAND_COLOR, '#126A6E'),
  splashBackgroundColor: readBrandingValue(
    import.meta.env.VITE_BRAND_SPLASH_BACKGROUND_COLOR,
    '#0D2A33',
  ),
  pushNotificationIconPath: '/synap-icon-192.png',
  pushNotificationBadgePath: '/synap-badge-96.png',
}
